// Prizes on the player screens (v2.1): what is on offer, how it is won, where the terms are and when the draw closes.
// Pure helpers, unit-tested. The legal wording itself lives in legal/content.ts and is only rendered, never rewritten.
import { PRIZE_DEFAULTS, type BattleTerms, type PrizeConfig, type StateSnapshot } from '@shared/types';

/** The prize summary that snapshots and the public info carry. */
export type PrizeSummary = NonNullable<StateSnapshot['prize']>;
type AnyPrize = Pick<PrizeConfig, 'enabled' | 'prize' | 'winners' | 'drawPrizes'>;

const HOUR_MS = 3_600_000;
const whole = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : fallback);

/** The battle's prize when one is switched on and named; anything else counts as no prize. */
export function activePrize(p: Partial<AnyPrize> | undefined | null): AnyPrize | undefined {
  if (!p || p.enabled !== true) return undefined;
  const prize = typeof p.prize === 'string' && p.prize.trim() ? p.prize.trim() : PRIZE_DEFAULTS.prize;
  return { enabled: true, prize, winners: Math.max(1, whole(p.winners, PRIZE_DEFAULTS.winners)), drawPrizes: whole(p.drawPrizes, PRIZE_DEFAULTS.drawPrizes) };
}

/**
 * The prize as it reads after a verb: "A 12-month MedSnapp subscription" becomes "a 12-month MedSnapp subscription",
 * so "Win a 12-month MedSnapp subscription." Only a leading article or number word is lowered; names keep their capitals.
 */
export function prizePhrase(text: string): string {
  const t = text.trim().replace(/[.\s]+$/, '');
  return /^(A|An|The|One|Two|Three|Four|Five|Six|Seven|Eight|Nine|Ten)\b/.test(t) ? t.charAt(0).toLowerCase() + t.slice(1) : t;
}

export const termsPath = (code: string) => `/terms/${encodeURIComponent(code.toUpperCase())}`;
export const PRIVACY_PATH = '/privacy';

/** When the claim form stops taking draw entries: the prize's claimHours after the battle ended (as the server works it out). */
export function claimClosesAt(t: Partial<Pick<BattleTerms, 'endedAt' | 'prize'>> | undefined): number | undefined {
  if (!t || typeof t.endedAt !== 'number') return undefined;
  const h = t.prize?.claimHours;
  const hours = typeof h === 'number' && Number.isFinite(h) && h >= 0 ? h : PRIZE_DEFAULTS.claimHours;
  return t.endedAt + hours * HOUR_MS;
}

/** "at 21:30 on 5 October", in UK time (the terms are written in UK time). */
export function closedAtLabel(at: number): string {
  try {
    const d = new Date(at);
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });
    const day = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', timeZone: 'Europe/London' });
    return `at ${time} on ${day}`;
  } catch { return ''; }
}
